/**
 * RNC View Details — original approved layout, fed by live RNC DB aggregation.
 * Primary: bnc.projects
 * Related: project_details, project_attributes, project_companies (+ companies),
 *          project_contacts (+ contacts), project_updates
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Check, Clock, Mail, RefreshCw, SkipForward, Zap, Building2, Users, Tags, MessageSquare, ArrowLeft, Tag, XCircle,
} from "lucide-react";
import PropTypes from "prop-types";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { signalsApi, outreachApi } from "@/lib/api";
import { applyJourneyToRnc, timelineStepState } from "@/lib/rncJourney";
import { canChangeLeadStatus, isQualifiedLeadStatus, assertLeadNotificationConfigured, leadStatusErrorMessage, toastLeadStatusUpdate } from "@/lib/leadStatus";
import {
  RNCS, statusColor, chInfo, flattenWorkflow,
} from "@/pages/signals/rnc/rncData";
import {
  RncShell, RncCrumb, ChannelBadge, AssignedWorkflowCard,
} from "@/pages/signals/rnc/RncShared";
import { ChangeLeadStatusModal } from "@/pages/signals/rnc/ChangeLeadStatusModal";
import { formatDateTime } from "@/lib/format";
import { signalsDashboardPath, SIGNALS_TAB_RNC } from "@/pages/signals/signalsNav";

const AVATAR_COLORS = ["#4F46E5", "#16A34A", "#DC2626", "#3B82F6", "#14B8A6", "#F97316", "#0A66C2", "#7C3AED"];

function fmt(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function initialsFromName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "PR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function pickTemplate(projectId) {
  if (!RNCS.length) return {};
  let hash = 0;
  const key = String(projectId || "");
  for (let i = 0; i < key.length; i += 1) hash = (hash + key.charCodeAt(i) * (i + 1)) % 997;
  return RNCS[hash % RNCS.length];
}

function getRncContactProps(primaryContact, location) {
  return {
    contact: primaryContact.full_name || location || "—",
    email: primaryContact.email || "—",
    phone: primaryContact.phone || "—",
  };
}

function getRncDates(project) {
  return {
    assignedDate: project.first_ingested_at || project.created_on_bnc || null,
    dateAdded: project.created_on_bnc || project.first_ingested_at || "—",
    lastActivity: project.last_synced_at || "—",
  };
}

function getRncProjectInfo(project, template) {
  return {
    type: project.sector || project.project_type || template.type || "Project",
    units: project.stage || template.units || "—",
  };
}

/** Map aggregated API payload into the existing RNC detail card shape. */
function buildRncViewModel(payload) {
  const project = payload?.project || {};
  const details = payload?.details;
  const contacts = payload?.contacts || [];
  const primaryContact = contacts[0] || {};
  const template = pickTemplate(project.project_id);
  const name = project.project_name || "Untitled project";
  const location = [project.city, project.country].filter(Boolean).join(", ");
  const cp = getRncContactProps(primaryContact, location);
  const dates = getRncDates(project);
  const info = getRncProjectInfo(project, template);

  return {
    id: project.project_id,
    company: name,
    initials: initialsFromName(name),
    color: template.color || AVATAR_COLORS[0],
    type: info.type,
    units: info.units,
    contact: cp.contact,
    email: cp.email,
    phone: cp.phone,
    // Live RNC DB projects always originate from RNC — never mock Quora/LinkedIn/etc.
    channel: "rnc",
    // Campaign/status filled by applyJourneyToRnc from Sellix enrollments.
    campaignId: null,
    campaignName: null,
    workflowId: null,
    workflowVersion: null,
    workflowAssigned: false,
    signalWorkflow: null,
    assignedDate: dates.assignedDate,
    stepIndex: 0,
    progress: null,
    status: null,
    currentStep: project.stage || null,
    nextStep: "None",
    remainingTime: "—",
    dateAdded: dates.dateAdded,
    lastActivity: dates.lastActivity,
    notes: (details && details.description) || project.internal_notes || "",
    city: project.city || "",
    country: project.country || "",
    stage: project.stage || "",
    bncReference: project.bnc_reference_number || "",
    sourceUrl: project.source_url || "",
    estValue: project.est_project_value || "",
    completion: project.completion_pct || "",
  };
}

function RelatedTable({ columns, rows, rowKey, emptyLabel }) {
  if (!rows?.length) {
    return <p style={{ color: "var(--slate-400)", fontSize: 13, margin: 0 }}>{emptyLabel}</p>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row[rowKey] || idx}>
              {columns.map((col) => (
                <td key={col.key} style={{ fontSize: 13 }}>
                  {fmt(typeof col.render === "function" ? col.render(row) : row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

RelatedTable.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array,
  rowKey: PropTypes.string,
  emptyLabel: PropTypes.string,
};

export default function RncDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, journey: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const projectId = decodeURIComponent(id || "").trim();
    setState({ loading: true, data: null, journey: null, error: null });
    Promise.all([
      signalsApi.getRmcProject(projectId),
      outreachApi.getRncJourney(projectId).catch(() => null),
    ])
      .then(([data, journey]) => {
        if (!cancelled) setState({ loading: false, data, journey, error: null });
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            loading: false,
            data: null,
            journey: null,
            error: e.message || "Failed to load project",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadToken]);

  const rnc = useMemo(() => {
    if (!state.data) return null;
    return applyJourneyToRnc(buildRncViewModel(state.data), state.journey);
  }, [state.data, state.journey]);
  const details = state.data?.details;
  const attributes = state.data?.attributes || [];
  const companies = state.data?.companies || [];
  const contacts = state.data?.contacts || [];
  const updates = state.data?.updates || [];

  if (state.loading) {
    return (
      <RncShell crumb={<RncCrumb company="Loading…" />}>
        <div className="card" style={{ padding: 20 }} data-testid="rnc-detail-loading">
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-40 w-full mb-3" />
          <Skeleton className="h-56 w-full" />
        </div>
      </RncShell>
    );
  }

  if (state.error || !rnc) {
    return (
      <RncShell crumb={<RncCrumb company="Project" />}>
        <div className="card" style={{ padding: 20 }} data-testid="rnc-detail-error">
          <p style={{ color: "#b91c1c", margin: 0 }}>{state.error || "Project not found"}</p>
          <p style={{ marginTop: 12 }}>
            <Link to={signalsDashboardPath(SIGNALS_TAB_RNC)} style={{ color: "var(--indigo)" }}>Return to RNC</Link>
          </p>
        </div>
      </RncShell>
    );
  }

  return (
    <InProgressView
      rnc={rnc}
      navigate={navigate}
      onRefresh={() => setReloadToken((n) => n + 1)}
      details={details}
      attributes={attributes}
      companies={companies}
      contacts={contacts}
      updates={updates}
    />
  );
}

function InProgressView({
  rnc, navigate, onRefresh, details, attributes, companies, contacts, updates,
}) {
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const progressPct = rnc.progress == null ? null : Math.round(Number(rnc.progress) * 100);
  const statusBadge = rnc.status || "Pending";
  const statusCls = rnc.status ? statusColor(rnc.status) : "gray";
  const showStatusBtn = canChangeLeadStatus(rnc);

  const fallbackActRows = [
    ["System", "Lead ingested from RNC", rnc.stage || "—", "gray"],
  ];

  const updateRows = updates.map((u) => [
    u.update_type || "Update",
    u.headline || u.body_text || "—",
    formatDateTime(u.update_date),
    "gray",
  ]);

  const actRows = updateRows.length ? updateRows : fallbackActRows;

  const handleStatusSubmit = async ({ lead_status, comment }) => {
    setStatusSaving(true);
    try {
      if (isQualifiedLeadStatus(lead_status)) {
        await assertLeadNotificationConfigured();
      }
      const result = await outreachApi.setEnrollmentLeadStatus(
        rnc.campaignId,
        rnc.enrollmentId,
        lead_status,
        comment,
      );
      toastLeadStatusUpdate(toast, result, lead_status);
      setStatusOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(leadStatusErrorMessage(e));
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <RncShell crumb={<RncCrumb company={rnc.company} />}>
      <div className="page-head" data-testid="rnc-project-detail-page">
        <div>
          <h1>{rnc.company}</h1>
          <p>
            {[rnc.city, rnc.country].filter(Boolean).join(", ") || "RNC project"}
            {" · "}
            sourced from RNC.
          </p>
          <div className="leadid-tag">RNC ID · {rnc.id}</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn"
            onClick={() => navigate(signalsDashboardPath(SIGNALS_TAB_RNC))}
            data-testid="rnc-detail-back"
          >
            <ArrowLeft size={15} /> Back
          </button>
          <span className={`badge ${statusCls}`}>
            <span className="dotb" />
            {statusBadge}
            {progressPct != null ? ` · ${progressPct}%` : ""}
          </span>
          {showStatusBtn ? (
            <button
              type="button"
              className="btn primary"
              onClick={() => setStatusOpen(true)}
              data-testid="rnc-change-status"
            >
              <Tag size={15} /> Change status
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onRefresh} data-testid="rnc-detail-refresh">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      <ChangeLeadStatusModal
        open={statusOpen}
        onOpenChange={setStatusOpen}
        company={rnc.company}
        rncId={rnc.id}
        currentLeadStatus={rnc.leadStatus}
        saving={statusSaving}
        onSubmit={handleStatusSubmit}
      />

      <div className="grid2">
        <div className="left-col card">
          <LeadCard rnc={rnc} />
          <div className="field">
            <label htmlFor={`notes-${rnc.id}`}>NOTES</label>
            <div id={`notes-${rnc.id}`} className="notes-box">
              {rnc.notes || (details ? "" : "No project details available.")}
            </div>
          </div>
          {rnc.statusComment ? (
            <div className="field" style={{ marginTop: 12 }}>
              <label htmlFor={`status-comment-${rnc.id}`}>STATUS COMMENT</label>
              <div id={`status-comment-${rnc.id}`} className="notes-box" data-testid="rnc-status-comment">
                {rnc.statusComment}
              </div>
            </div>
          ) : null}
          {(details?.num_companies != null || rnc.estValue) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
              {rnc.estValue ? (
                <div className="field">
                  <label htmlFor={`pval-${rnc.id}`}>PROJECT VALUE</label>
                  <div id={`pval-${rnc.id}`} className="val">{rnc.estValue}</div>
                </div>
              ) : null}
              {rnc.completion ? (
                <div className="field">
                  <label htmlFor={`pcomp-${rnc.id}`}>COMPLETION</label>
                  <div id={`pcomp-${rnc.id}`} className="val">{rnc.completion}</div>
                </div>
              ) : null}
              {details?.num_companies != null ? (
                <div className="field">
                  <label htmlFor={`pcos-${rnc.id}`}>COMPANIES</label>
                  <div id={`pcos-${rnc.id}`} className="val">{fmt(details.num_companies)}</div>
                </div>
              ) : null}
              {details?.num_owners != null ? (
                <div className="field">
                  <label htmlFor={`pown-${rnc.id}`}>OWNERS</label>
                  <div id={`pown-${rnc.id}`} className="val">{fmt(details.num_owners)}</div>
                </div>
              ) : null}
            </div>
          )}
          <div className="left-col-footer">
            <RefreshCw size={12} /> Synced {formatDateTime(rnc.lastActivity)} · Record {rnc.id}
          </div>
        </div>

        <div>
          <AssignedWorkflowCard
            rnc={rnc}
            onViewWorkflow={() => {
              navigate(`/signals/rnc/${encodeURIComponent(rnc.id)}/workflow`);
            }}
          />
          <div className="card">
            <h3><Zap size={16} /> AI Outreach Progress</h3>
            <WorkflowTimeline
              workflow={rnc.signalWorkflow}
              rncStatus={rnc.status}
              stepIndex={rnc.stepIndex}
              exited={Boolean(rnc.exited)}
              stepFailures={rnc.stepFailures || {}}
              stepSkips={rnc.stepSkips || {}}
              waiting={Boolean(rnc.workflowWaiting)}
            />
          </div>

          <div className="card" data-testid="rnc-section-updates">
            <h3><MessageSquare size={16} /> Project Updates</h3>
            <UpdatesTable actRows={actRows} />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 22 }} data-testid="rnc-section-companies">
        <h3><Building2 size={16} /> Companies</h3>
        <RelatedTable
          rowKey="id"
          rows={companies}
          emptyLabel="No companies linked to this project."
          columns={[
            { key: "company_name", label: "Company" },
            { key: "role_label", label: "Role", render: (r) => r.role_label || r.role_category },
            { key: "status", label: "Status" },
            { key: "award_value", label: "Award" },
            { key: "company_city", label: "City" },
            { key: "company_country", label: "Country" },
          ]}
        />
      </div>

      <div className="card" style={{ marginTop: 14 }} data-testid="rnc-section-contacts">
        <h3><Users size={16} /> Contacts</h3>
        <RelatedTable
          rowKey="id"
          rows={contacts}
          emptyLabel="No contacts linked to this project."
          columns={[
            { key: "full_name", label: "Name" },
            { key: "title_role", label: "Title" },
            { key: "role_on_project", label: "Role on project" },
            { key: "company_name", label: "Company" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
          ]}
        />
      </div>

      <div className="card" style={{ marginTop: 14 }} data-testid="rnc-section-attributes">
        <h3><Tags size={16} /> Attributes</h3>
        <RelatedTable
          rowKey="id"
          rows={attributes}
          emptyLabel="No attributes recorded for this project."
          columns={[
            { key: "attribute_name", label: "Attribute" },
            { key: "attribute_value", label: "Value" },
          ]}
        />
      </div>
    </RncShell>
  );
}

function LeadCard({ rnc }) {
  return (
    <>
      <div className="lead-hero">
        <div className="mk" style={{ background: rnc.color }}>{rnc.initials}</div>
        <div>
          <div className="nm">{rnc.company}</div>
          <div className="sub">{rnc.type} · {rnc.units}</div>
        </div>
      </div>
      <div className="field idfield">
        <label htmlFor={`rnc-id-${rnc.id}`}>RNC ID</label>
        <div id={`rnc-id-${rnc.id}`} className="val">{rnc.id}</div>
      </div>
      <div className="field">
        <label htmlFor={`contact-${rnc.id}`}>CONTACT PERSON</label>
        <div id={`contact-${rnc.id}`} className="val">{rnc.contact}</div>
      </div>
      <div className="field">
        <label htmlFor={`email-${rnc.id}`}>EMAIL</label>
        <div id={`email-${rnc.id}`} className="val link">{rnc.email}</div>
      </div>
      <div className="field">
        <label htmlFor={`phone-${rnc.id}`}>PHONE NUMBER</label>
        <div id={`phone-${rnc.id}`} className="val">{rnc.phone}</div>
      </div>
      <div className="field">
        <label htmlFor={`location-${rnc.id}`}>LOCATION</label>
        <div id={`location-${rnc.id}`} className="val">
          {[rnc.city, rnc.country].filter(Boolean).join(", ") || "—"}
        </div>
      </div>
      <div className="field">
        <label htmlFor={`stage-${rnc.id}`}>STAGE</label>
        <div id={`stage-${rnc.id}`} className="val">{rnc.stage || "—"}</div>
      </div>
      <div className="field">
        <label htmlFor={`channel-${rnc.id}`}>SOURCE</label>
        <div id={`channel-${rnc.id}`} className="val" style={{ display: "flex", alignItems: "center", gap: 7 }} data-testid="lead-source">
          {rnc.channel ? (
            <>
              <ChannelBadge channelKey={rnc.channel} size={18} /> {chInfo(rnc.channel).name}
            </>
          ) : (
            "N/A"
          )}
        </div>
      </div>
      <div className="field">
        <label htmlFor={`date-added-${rnc.id}`}>DATE ADDED</label>
        <div id={`date-added-${rnc.id}`} className="val">{formatDateTime(rnc.dateAdded)}</div>
      </div>
    </>
  );
}

function UpdatesTable({ actRows }) {
  if (!actRows || !actRows.length) {
    return <p style={{ color: "var(--slate-400)", fontSize: 13, margin: 0 }}>No updates yet.</p>;
  }
  return (
    <table>
      <thead>
        <tr><th>TYPE</th><th>DETAIL</th><th>DATE</th><th>STATUS</th></tr>
      </thead>
      <tbody>
        {actRows.map(([ch, act, res, c]) => (
          <tr key={`${ch}-${act}-${res}`}>
            <td><b>{ch}</b></td>
            <td>{act}</td>
            <td>{res}</td>
            <td>
              <span className={`badge ${c === "purple" ? "blue" : c}`}>
                <span className="dotb" />
                {c === "purple" ? "Sent" : "Logged"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
UpdatesTable.propTypes = {
  actRows: PropTypes.array.isRequired,
};

function WorkflowTimeline({ workflow, rncStatus, stepIndex, exited, stepFailures, stepSkips, waiting = false }) {
  const flat = flattenWorkflow(workflow);
  if (!flat.length) {
    return (
      <p style={{ color: "var(--slate-400)", fontSize: 13, margin: 0 }}>
        No workflow assigned yet for this RNC lead.
      </p>
    );
  }
  return (
    <div className="timeline">
      {exited && (
        <p
          style={{ color: "var(--slate-500)", fontSize: 13, margin: "0 0 14px" }}
          data-testid="rnc-timeline-exited-note"
        >
          This lead exited the campaign — remaining steps were cancelled.
        </p>
      )}
      {flat.map((s, i) => {
        const { cls, when: whenText, stopped, error } = timelineStepState({
          index: i,
          stepIndex,
          status: rncStatus,
          exited,
          stepId: s.id,
          stepFailures,
          stepSkips,
          stepBadge: s.badge,
          waiting,
        });

        let IconComp = <Mail size={12} />;
        if (cls === "done") IconComp = <Check size={12} strokeWidth={3} />;
        else if (cls === "active") IconComp = <Clock size={12} />;
        else if (cls === "skipped") IconComp = <SkipForward size={12} />;
        else if (cls === "failed" || stopped) IconComp = <XCircle size={12} />;

        return (
          <div className={`tstep ${cls}`} key={`${s.id || s.title}-${i}`}>
            <div className={`node ${cls}`}>{IconComp}</div>
            <div className="body">
              <div className="row1">
                <span className="ttl">{s.title}</span>
                <span className="when">{whenText}</span>
              </div>
              <div className="desc">
                {s.branchLabel && <span className="tag purple">{s.branchLabel}</span>}
                {s.detail || ""}
              </div>
              {error && cls === "failed" ? <div className="step-error">{error}</div> : null}
              {error && cls === "skipped" ? <div className="step-skip">{error}</div> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
WorkflowTimeline.propTypes = {
  workflow: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  rncStatus: PropTypes.string,
  stepIndex: PropTypes.number,
  exited: PropTypes.bool,
  stepFailures: PropTypes.object,
  stepSkips: PropTypes.object,
  waiting: PropTypes.bool,
};

const rncPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  company: PropTypes.string,
  channel: PropTypes.string,
  status: PropTypes.string,
  workflowId: PropTypes.string,
  workflowVersion: PropTypes.string,
  workflowAssigned: PropTypes.bool,
  signalWorkflow: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  campaignId: PropTypes.string,
  stepIndex: PropTypes.number,
  progress: PropTypes.number,
  notes: PropTypes.string,
  lastActivity: PropTypes.string,
  contact: PropTypes.string,
  color: PropTypes.string,
  initials: PropTypes.string,
  type: PropTypes.string,
  units: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  dateAdded: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  stage: PropTypes.string,
  estValue: PropTypes.string,
  completion: PropTypes.string,
  enrollmentId: PropTypes.string,
  leadStatus: PropTypes.string,
  statusComment: PropTypes.string,
  exited: PropTypes.bool,
  stepFailures: PropTypes.object,
  stepSkips: PropTypes.object,
});

InProgressView.propTypes = {
  rnc: rncPropType.isRequired,
  navigate: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  details: PropTypes.object,
  attributes: PropTypes.array,
  companies: PropTypes.array,
  contacts: PropTypes.array,
  updates: PropTypes.array,
};

LeadCard.propTypes = {
  rnc: rncPropType.isRequired,
};
